import { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as Yup from 'yup';
import { Formik, Form, FormikProps } from 'formik';
import FormikControl from './components/FormikContainer/FormikControl';
import PlacesAutocomplete, { getAddressComponents } from './components/PlacesAutoComplete';
import SectionHeader from './components/SectionHeader';
import PrimaryButton from './components/PrimaryButton/PrimaryButton';
import SchoolEditorModal from './components/SchoolEditorModal';
import { updateAllFields } from './UserManagement';
import { SpinnerOverlay } from './SpinnerOverlay';
import {
  getAuthorizationToken,
  updateUserProfile,
  updateClassroom as updateClassroomBackend,
} from './BackendInterface';
import { selectClassroom, updateClassroom } from './Classrooms';
import { getLetterGradeFromNumberGrade, getRecommendedReadingLevelFromGrade, noWhiteSpace } from './GlobalFunctions';
import { ApiCallErrorMessage } from './ApiCallErrorMessage';
import { logger } from './error-tracker';
import styles from './SCSS/MoreInformationSchool.module.scss';
import { postServiceRequest } from './serviceAgent';
import { getAppRoot } from './identity';
import { cloneDeep } from 'lodash';
import { ClassroomType, UserProfileType } from '../peekapak-types/DataProtocolTypes';
import { AppDispatch, RootState } from './ApplicationState';
import { MainLayoutPropagatingProps } from './MainLayout';

const RoleValues = [
  { key: 'Please select your role...', value: 'placeholder' },
  { key: 'Teacher', value: 'Teacher' },
  { key: 'Principal', value: 'Principal' },
  { key: 'District Administrator', value: 'District Administrator' },
  { key: 'School Administrator', value: 'School Administrator' },
  { key: 'Librarian', value: 'Librarian' },
  { key: 'Counselor', value: 'Counselor' },
  { key: 'SEL or PBIS Lead', value: 'SEL or PBIS Lead' },
  { key: 'Homeschool Teacher', value: 'Homeschool Teacher' },
  { key: 'Parent', value: 'Parent' },
  { key: 'Other', value: 'Other' },
];

const GradeValues = [
  { key: 'Please select the grade range you teach...', value: 'placeholder' },
  { key: 'PK-5', value: '2' },
  { key: '6-8', value: '6' },
  { key: '9-12', value: '9' },
  // { key: 'PK-12', value: '' },
];

interface FormValues {
  role: string;
  grade: string;
  schoolAddress: string;
}

const mapStateToProps = (state: RootState) => ({
  classroom: selectClassroom(state),
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateClassroom: (classroom: ClassroomType) => dispatch(updateClassroom(classroom)),
  updateUserProfile: (newUserProfile: Partial<UserProfileType>) => dispatch(updateAllFields(newUserProfile)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type MoreInformationSchoolProps = PropsFromRedux & MainLayoutPropagatingProps;

const AutocompleteItem = ({
  formattedSuggestion,
}: {
  formattedSuggestion: { mainText: string; secondaryText: string };
}) => (
  <div>
    <i className='fa fa-map-marker' />
    &nbsp;
    <strong>{formattedSuggestion.mainText}</strong>{' '}
    <span
      style={{
        fontFamily: 'GT-Walsheim-Pro-Regular',
      }}
    >
      <small>{formattedSuggestion.secondaryText}</small>
    </span>
  </div>
);

const cssClasses = {
  root: 'form-group',
  input: 'form-control',
};
const myStyles = {
  autocompleteItemActive: {
    backgroundColor: '#d9d9d9',
  },
};
type StateType = {
  schoolNameOrAddress: string;
  schoolNameOrAddressValidationState: string | null | undefined;
  schoolNameOrAddressValidationHint: string;
  schoolName: string;
  schoolAddress: string;
  schoolState: string;
  schoolCity: string;
  schoolZip: string;
  country: string;
  placeId: string;
  tel: string;
  role: string;
  grade: string;
  roleValidationState: string | null | undefined;
  roleValidationHint: string;
  isEditingSchoolInfo: boolean;
  isHomeSchool: boolean;
  isModalButtonClick: boolean;
  isPlacesListShowing: boolean;
  isShowSpinner: boolean;
  isApiCallReturned: boolean;
  apiResponse: string;
  isShowErrorModal: boolean;
};

const MoreInformationSchool = (props: MoreInformationSchoolProps) => {
  const [myState, setMyState] = useState<StateType>({
    schoolNameOrAddress: '',
    schoolNameOrAddressValidationState: null,
    schoolNameOrAddressValidationHint: '',
    schoolAddress: '',
    schoolName: '',
    schoolCity: '',
    schoolState: '',
    schoolZip: '',
    country: '',
    placeId: '',
    tel: '',
    role: '',
    grade: '',
    roleValidationState: null,
    roleValidationHint: '',
    isEditingSchoolInfo: false,
    isHomeSchool: false,
    isModalButtonClick: false,
    isPlacesListShowing: false,
    isShowSpinner: false,
    isApiCallReturned: false,
    apiResponse: '',
    isShowErrorModal: false,
  });

  const initialFormValues: FormValues = {
    role: '',
    grade: '',
    schoolAddress: '',
  };

  const isTrialUser = props.userProfile?.licenseLevel.includes('TRIAL');

  const formikRef = useRef<FormikProps<FormValues>>(null);

  const validationSchema = Yup.object({
    role: Yup.string().required('Please select your role'),
    grade: Yup.string().when([], {
      is: () => !isTrialUser,
      then: Yup.string().required('Please select a grade range'),
      otherwise: Yup.string().notRequired(),
    }),
    schoolAddress: Yup.string().when([], {
      is: () => !myState.isHomeSchool,
      then: Yup.string().required('Please enter your school name'),
      otherwise: Yup.string().notRequired(),
    }),
  });

  useEffect(() => {
    // console.debug(
    //   `%cuseEffect on schoolName presence check called`,
    //   'background: blue; color: yellow'
    // );
    if (props.userProfile && props.userProfile.schoolName) {
      props.history.replace('/library');
    }
  }, [props.userProfile, props.history]);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.setFieldValue('schoolAddress', myState.schoolNameOrAddress);
    }
  }, [myState.schoolNameOrAddress]);

  return (
    <div className={styles.moreInformationSchool}>
      <SectionHeader
        title='Missing School Name'
        copy='You&rsquo;re almost ready to enter the world of Peekapak! We just need to know your role and the name of the school you are associated with.'
        extraSmall
        center
      >
        <p className={styles.copy}>
          If your school cannot be found in the search below,{' '}
          <span className={styles.textLink} onClick={(event) => onModalButtonClick(event)}>
            please provide us the information here
          </span>
          .
        </p>
      </SectionHeader>
      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          const placesInputProps = {
            name: 'schoolAddress',
            value: myState.schoolNameOrAddress,
            onChange: (schoolNameOrAddress: string) => onSchoolNameOrAddressChange(schoolNameOrAddress, setFieldValue),
            type: 'search',
            placeholder: 'Type in your school name or address...',
            autoFocus: false,
            autoComplete: 'off',
          };
          return (
            <Form className={styles.form}>
              <FormikControl
                control='select'
                label='Your Role*'
                name='role'
                options={RoleValues}
                onChange={onRoleChange}
              />
              {!isTrialUser && (
                <FormikControl
                  control='select'
                  label='Grade range you’re teaching with Peekapak*'
                  name='grade'
                  options={GradeValues}
                  onChange={onGradeChange}
                />
              )}
              {!myState.isHomeSchool && (
                <PlacesAutocomplete
                  inputProps={placesInputProps}
                  autocompleteItem={AutocompleteItem}
                  classNames={cssClasses}
                  onSelect={(address, place) => onPlacesSelect(address, place, setFieldValue)}
                  onError={onPlacesError}
                  onShowList={onShowList}
                  styles={myStyles}
                  onEnterKeyDown={handleOnEnterFromPlacesAutocomplete}
                  clearItemsOnError
                  validate={isSchoolValid}
                />
              )}
              {myState.schoolNameOrAddressValidationHint &&
                myState.schoolNameOrAddressValidationState !== 'success' && (
                  <>
                    <p className={styles.schoolNameOrAddressValidationHint}>
                      {myState.schoolNameOrAddressValidationHint}
                    </p>
                    <div className={styles.schoolEditorModal} data-show={myState.isEditingSchoolInfo}>
                      <PrimaryButton
                        orange
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                          onModalButtonClick(event);
                        }}
                      >
                        Manually Enter Your School&apos;s Info
                      </PrimaryButton>
                    </div>
                  </>
                )}
              <SubmitButton />
            </Form>
          );
        }}
      </Formik>
      <SchoolEditorModal
        className='ModalDialog'
        show={myState.isEditingSchoolInfo && myState.isModalButtonClick}
        onHide={closeModal}
        schoolName={myState.schoolNameOrAddress}
        onUpdate={onUpdate}
      />
      <SpinnerOverlay isShow={myState.isShowSpinner} />
      <ApiCallErrorMessage
        isShow={myState.isShowErrorModal}
        onHide={handleHideErrorModal}
        apiResponseText={myState.apiResponse}
      />
    </div>
  );

  function handleHideErrorModal() {
    setMyState({ ...myState, isShowErrorModal: false });
  }

  function SubmitButton() {
    return (
      <>
        {!myState.isPlacesListShowing && isInputValid() && (
          <PrimaryButton className={styles.submitButton} type='submit'>
            Submit
          </PrimaryButton>
        )}
      </>
    );
  }

  function handleOnEnterFromPlacesAutocomplete(inputSchoolName: string) {
    setMyState({ ...myState, schoolName: inputSchoolName });
    if (inputSchoolName.length > 0) {
      isSchoolValid(inputSchoolName);
    }
  }

  function isInputValid() {
    const { schoolNameOrAddressValidationState, roleValidationState } = myState;
    return schoolNameOrAddressValidationState !== 'error' && roleValidationState !== 'error';
  }

  function onRoleChange(value: string) {
    if (value === 'Homeschool Teacher') {
      setMyState({
        ...myState,
        role: value,
        schoolName: 'Homeschool',
        isHomeSchool: true,
        schoolNameOrAddressValidationState: null,
        schoolNameOrAddressValidationHint: '',
      });
    } else {
      setMyState({
        ...myState,
        role: value,
        isHomeSchool: false,
      });
    }
  }

  async function onGradeChange(value: string) {
    setMyState({
      ...myState,
      grade: value,
    });
    const grade = getLetterGradeFromNumberGrade(Number(value));
    await updateClassroomBasedOnGrade(grade);
  }

  function onSchoolNameOrAddressChange(
    schoolNameOrAddress: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
  ) {
    setMyState({
      ...myState,
      schoolNameOrAddress,
      schoolNameOrAddressValidationState: null,
      schoolNameOrAddressValidationHint: '',
    });
    setFieldValue('schoolAddress', schoolNameOrAddress);
  }

  function onShowList(showStatus: boolean) {
    setMyState({ ...myState, isPlacesListShowing: showStatus });
  }

  function onPlacesError(status: string) {
    // console.debug(
    //   `%conPlacessError: `,
    //   'background: blue; color: yellow',
    //   status
    // );
  }

  function onPlacesSelect(
    address: string,
    placeId: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
  ) {
    const request = {
      placeId,
    };
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails(request, callback);

    function callback(place: google.maps.places.PlaceResult | null, status: google.maps.places.PlacesServiceStatus) {
      if (!place) {
        throw new Error('PlaceResult is null');
      }
      const AddressComponent = getAddressComponents(place, status);

      if (AddressComponent) {
        if (AddressComponent.isSchool) {
          setStateForSchool(place);
        } else {
          setStateForNonSchoolAddress();
        }
      }

      function setStateForSchool(place: google.maps.places.PlaceResult) {
        setMyState({
          ...myState,
          schoolName: place.name || 'Unkown',
          schoolNameOrAddress: place?.name as string,
          schoolAddress: AddressComponent.address,
          placeId: place?.place_id as string,
          schoolCity: AddressComponent.city,
          schoolState: AddressComponent.state,
          schoolZip: AddressComponent.zip || AddressComponent.postal_code || '',
          country: AddressComponent.country,
          isPlacesListShowing: false,
        });
        setFieldValue('schoolAddress', place?.name);
      }

      function setStateForNonSchoolAddress() {
        setMyState({
          ...myState,
          schoolName: place?.name as string,
          schoolAddress: AddressComponent.address,
          schoolNameOrAddress: place?.name as string,
          placeId: place?.place_id as string,
          schoolCity: AddressComponent.city,
          schoolState: AddressComponent.state,
          schoolZip: AddressComponent.zip || AddressComponent.postal_code || '',
          country: AddressComponent.country,
          isPlacesListShowing: false,
        });
        setFieldValue('schoolAddress', place?.name);
      }
    }
  }

  async function updateClassroomBasedOnGrade(grade: string) {
    const classroomClone = cloneDeep(props.classroom);
    const readingLevel = getRecommendedReadingLevelFromGrade(parseInt(grade, 10), 'en');
    if (!readingLevel) {
      throw new Error('Reading Level is undefined');
    }
    classroomClone.readingLevel = readingLevel.toString();
    classroomClone.grade = grade;

    const classroomToUpdate = (() => {
      if (classroomClone.completionStatus) {
        const modifiedClassroom = { ...classroomClone };
        delete modifiedClassroom.completionStatus;
        return modifiedClassroom;
      }
      return classroomClone;
    })();

    const { email } = props.userProfile as UserProfileType;

    if (!email) {
      console.error('MoreInformationSchool::updateClassroomBasedOnGrade - update failed - email is undefined');
      return;
    }

    try {
      const updatedClassroomResult = await updateClassroomBackend(
        email,
        classroomToUpdate.className,
        classroomToUpdate,
        true,
      );
      props.updateClassroom(updatedClassroomResult);
    } catch (error) {
      console.error('MoreInformationSchool::updateClassroomBasedOnGrade - update failed ', error);
    }
  }

  async function saveData() {
    // console.debug(`%cSave Data`, 'background: blue; color: yellow');
    setMyState({ ...myState, isShowSpinner: true });
    const requestData = {
      email: props.userProfile?.email || '',
      role: myState.role,
      grade: Number(myState.grade),
      country: myState.country,
      schoolZip: myState.schoolZip,
      schoolName: myState.schoolName || '',
      tel: myState.tel || '',
      schoolCity: myState.schoolCity || '',
      schoolState: myState.schoolState || '',
      placeId: myState.placeId || '',
      schoolAddress: myState.schoolAddress || '',
    };

    try {
      await updateUserProfile({ newUserData: requestData });
      props.updateUserProfile(requestData);
      setMyState({
        ...myState,
        isApiCallReturned: true,
        isShowSpinner: false,
      });
      return true;
    } catch (error) {
      setMyState({
        ...myState,
        isApiCallReturned: true,
        apiResponse: (error as Error).message,
        isShowSpinner: false,
        isShowErrorModal: true,
      });
      throw error;
    }
  }

  function redirectToLibrary() {
    props.history.replace('/library');
  }

  async function handleSubmit(values: FormValues) {
    setMyState({
      ...myState,
      schoolName: myState.schoolNameOrAddress.trim(),
      schoolZip: myState.schoolZip.trim(),
      country: myState.country,
      tel: myState.tel.trim(),
      grade: myState.grade,
    });
    try {
      await saveData();
      redirectToLibrary();
    } catch (error) {
      logger.logException(error as Error);
    }
  }

  function isSchoolValid(value: string) {
    const { schoolNameOrAddress, schoolZip, schoolName, isHomeSchool } = myState;
    if ((schoolName !== schoolNameOrAddress && schoolNameOrAddress !== '') || schoolZip === '') {
      setMyState({
        ...myState,
        schoolNameOrAddressValidationState: 'error',
        schoolNameOrAddressValidationHint: noWhiteSpace`We cannot find this school. Please try typing
          in the school name again, or manually enter your school by using the button below:`,
        isEditingSchoolInfo: true,
      });
      return false;
    }
    if (isHomeSchool) {
      setMyState({
        ...myState,
        schoolNameOrAddressValidationState: null,
        schoolNameOrAddressValidationHint: '',
        isEditingSchoolInfo: false,
      });
    }
    setMyState({
      ...myState,
      schoolNameOrAddressValidationState: null,
      schoolNameOrAddressValidationHint: '',
      isEditingSchoolInfo: false,
    });
    return true;
  }

  function closeModal() {
    setMyState({
      ...myState,
      isEditingSchoolInfo: true,
      isModalButtonClick: false,
    });
    return true;
  }

  function onModalButtonClick(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();
    setMyState({
      ...myState,
      isModalButtonClick: true,
      isEditingSchoolInfo: true,
    });
  }

  function onUpdate(data: {
    schoolName: string;
    country: string;
    schoolState: string;
    schoolCity: string;
    schoolZip: string;
  }) {
    setMyState({
      ...myState,
      schoolName: data.schoolName,
      schoolNameOrAddress: data.schoolName,
      schoolCity: data.schoolCity,
      schoolState: data.schoolState,
      country: data.country,
      schoolZip: data.schoolZip,
      isEditingSchoolInfo: true,
      isModalButtonClick: false,
      schoolNameOrAddressValidationState: 'success',
      schoolNameOrAddressValidationHint: '',
    });
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(MoreInformationSchool);
